import { useEffect, useRef, type ElementType, type HTMLAttributes } from "react";
import { isPrerender } from "@/lib/prerender";

/**
 * Text that decodes out of binary when it scrolls into view (and again on
 * hover, if `onHover`), after the 1s and 0s in the logo.
 *
 * Meant for monospace labels only: every glyph is the same width, so the
 * scramble never reflows the line. The real text is in the DOM from the first
 * render, so the prerendered HTML, screen readers and visitors who prefer
 * reduced motion all get it as-is.
 */
const GLYPHS = "01";
const FRAME_MS = 28;

type ScrambleProps = HTMLAttributes<HTMLElement> & {
  text: string;
  as?: ElementType;
  /** Replay the decode when the pointer enters. */
  onHover?: boolean;
};

const Scramble = ({ text, as: Tag = "span", onHover = false, ...rest }: ScrambleProps) => {
  const ref = useRef<HTMLElement>(null);
  const timer = useRef<number>(0);

  const run = () => {
    const el = ref.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    window.clearInterval(timer.current);
    let frame = 0;
    // Each character resolves a little after the one before it.
    const total = text.length + 8;
    timer.current = window.setInterval(() => {
      frame += 1;
      let out = "";
      for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        if (ch === " " || i < frame - 8) out += ch;
        else if (i < frame) out += GLYPHS[(Math.random() * GLYPHS.length) | 0];
        else out += ch === " " ? " " : " ";
      }
      el.textContent = out;
      if (frame >= total) {
        window.clearInterval(timer.current);
        el.textContent = text;
      }
    }, FRAME_MS);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el || isPrerender() || !("IntersectionObserver" in window)) return;
    // Labels already on screen at load stay as rendered; only ones scrolled to
    // later decode, so the first paint never flickers.
    if (el.getBoundingClientRect().top < window.innerHeight) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        run();
      },
      { rootMargin: "0px 0px -10% 0px" }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      window.clearInterval(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <Tag
      ref={ref}
      aria-label={text}
      onMouseEnter={onHover ? run : undefined}
      {...rest}
    >
      {text}
    </Tag>
  );
};

export default Scramble;
